import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import Entity from '../Common/Entity';
import { DeleteOrderAction, getpaginatePOAction, ModifyOrderAction } from './API/Action';
import CustomChip from '../Components/CustomChip';
import { toast } from 'react-toastify';
import { getVerificationStatusLabel, ORDER_STATUS } from '../../utils/settings';
import StatusBadge from '../../v2/MUI_custom_component/Badge/StatusBadge';
import { CustomButton } from '../../v2/MUI_custom_component';
import { useSelector } from 'react-redux';
import EditPOModal from './Components/EditPOModal';
import DeleteModal from '../../v2/MUI_custom_component/Modal/DeleteModal';
import { useNavigate } from 'react-router-dom';
import ViewAttachedDP from './ViewAttachedDP';

const PAGE_SIZE = 10;

const POList = () => {
    const navigate = useNavigate();
    const [poList, setPoList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [pageNumber, setPageNumber] = useState(1);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [dateFilter, setDateFilter] = useState('');
    const [openEditPOModal, setOpenEditPOModal] = useState(false);
    const [openDeletePOModal, setOpenDeletePOModal] = useState(false);
    const [poDetails, setPoDetails] = useState({});
    const [openViewAttachedDP, setOpenViewAttachedDP] = useState(false);

    const { selectedSupplierData, selectedSupplier: selectedManufacturer, selectedEntity: selectedCompanyEntity } = useSelector(
        (state) => state.entityReducer
    );
    const statusOptions = ['pending', 'approved', 'partially_completed', 'completed', 'cancelled'];
    const statusLabels = {
        pending: 'Pending',
        approved: 'Approved',
        partially_completed: 'Partially Completed', 
        completed: 'Completed',
        cancelled: 'Cancelled'
    };
    const dateFilters = [
        { value: '3months', label: 'Past 3 months' },
        { value: '1month', label: 'Past 1 month' },
    ];
    const getStartEndDate = useCallback(() => {
        const now = dayjs();
        switch (dateFilter) {
        case '1month':
            return {
            startDate: now.subtract(1, 'month').toDate(),
            endDate: now.toDate()
            };
        case '3months':
            return {
            startDate: now.subtract(3, 'month').toDate(),
            endDate: now.toDate()
            };
        default:
            return { startDate: null, endDate: null };
        }
    }, [dateFilter]);

    const fetchPOList = async (reset = false) => {
        const config = new URLSearchParams();
        config.append('f', 'id$gte$0');
        config.append('o', 'id$desc');
        config.append('page', reset ? '1' : pageNumber.toString());
        config.append('page_size', PAGE_SIZE.toString());
        config.append('is_buyer', 'true');
    
        if (selectedCompanyEntity) {
            config.append('f', `buyer_esi_id$eq$${selectedCompanyEntity}`);
        }
        console.log('selectedCompanyEntity', selectedCompanyEntity);
        if (selectedStatus.length > 0) {
            config.append('f', `final_status$in$${selectedStatus.join('~')}`);
        }
        const startEndDate = getStartEndDate();
        if (startEndDate.startDate && startEndDate.endDate) {
            config.append(
                'f',
                `documented_at$gte$${dayjs(startEndDate.startDate)
                .startOf('day')
                .format('YYYY-MM-DD HH:mm:ssZ')}`,
            );
            config.append(
                'f',
                `documented_at$lte$${dayjs(startEndDate.endDate)
                .endOf('day')
                .format('YYYY-MM-DD HH:mm:ssZ')}`,
            );
        }
        setLoading(true);
        const {data, error, message} = await getpaginatePOAction(config);
        setLoading(false);
        if(!error){
            setPoList(reset ? data : [...poList, ...data]);
            setHasMore(data.length === PAGE_SIZE);
            if (reset) setPageNumber(2);
        }else{
            setPageNumber(pageNumber + 1);
            toast.error(message || 'Failed to fetch purchase orders');
        }
    };
    const handleStatusFilter = (status) => {
        const newSelectedStatus = selectedStatus.includes(status)
        ? selectedStatus.filter(s => s !== status)
        : [...selectedStatus, status];
        setSelectedStatus(newSelectedStatus);
    };
    const OnPressEditPO = (po) => {
        setPoDetails(po);
        setOpenEditPOModal(true);
    };
    const OnPressCreateDP = () => {
        navigate('/create-dispatch-plan');
    };
    const onViewDPAttached = (po) => {
        setPoDetails(po);
        setOpenViewAttachedDP(true);
    };
    const onCancelPO = (po) => {
        setPoDetails(po);
        setOpenDeletePOModal(true);
    };
    const handleDeletePO = async () => {
        const config = new URLSearchParams();
        config.append('order_booking_id', poDetails.humbee_po_number);
        config.append('user_esi_id', selectedCompanyEntity,);
        const { error, message } = await DeleteOrderAction(config);
        if (!error) {
            toast.success(message);
            setOpenDeletePOModal(false);
            fetchPOList(true);
        } else {
            toast.error(message);
        }
    };
    useEffect(() => {
        if(selectedCompanyEntity){
            fetchPOList(true);
        }
    }, [selectedCompanyEntity, selectedStatus, dateFilter]);
    const handleEditOrder = async (editData) => {
        const payload = {
            order_booking_id: editData.poDetails?.humbee_po_number,
            user_po_number: editData.userPONumber,
            user_esi_id: selectedCompanyEntity,
            product_list: [
                    {
                        product_id: editData.poDetails.quantity_info[0].product_id,
                        quantity: {
                            quantity_in_metric_ton: Number(editData.quantity),
                            multiplier: editData.poDetails.quantity_info[0].mulitplier_id,
                        },
                    },
                ],
        };
        const { error, message } = await ModifyOrderAction(payload);
        if (!error) {
            toast.success(message);
        }else{
            toast.error(message);
        }
        
        setOpenEditPOModal(false);
        fetchPOList(true);
    };
    const renderPOItem = (po, index) => (
        <Box key={`${po.id}-${index}`} sx={{
            p: 2,
            backgroundColor: '#fff',
            borderRadius: 2,
            display: 'flex',
            gap: 2,
            flexDirection: 'column',
        }}>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { md: 'center' }}}>
                    <Typography variant="bodyL" sx={{ borderRight: {md:'1px solid #D9D9D9'}, minWidth: '110px',}}>
                        {dayjs(po.documented_at).format('DD MMM YYYY')}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%'
                        }}
                    >
                            <Typography variant="bodyL">
                                {po.user_po_number || 'PO1234567'}
                            </Typography>
                            <StatusBadge 
                                label={getVerificationStatusLabel(po.status)} 
                                status={po.status} 
                            />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' }, width: '100%', gap: 2}}>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'row', }, 
                            justifyContent: {xs: 'space-between', md: 'flex-start'},
                            width: '100%',
                            gap: 2,
                            ml: { md: '125px' },
                        }}
                    >
                        {po.quantity_info && (
                            <Box sx={{ gap: 0.5, display: 'flex', flexDirection: 'column', width: {md: '50%'} }}>
                                {po.quantity_info.map(item => (
                                    <Box key={item.product_id} sx={{ display: 'flex', gap: 2 }}>
                                        <Typography variant="labelM" sx={{ minWidth: { md: '100px'}, color: '#666' }}>
                                            {item.label}
                                        </Typography>
                                        <Typography variant="p3" fontWeight={600}>
                                            {item.value} {item.unit}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {po.quantity_info && po.price_info && (
                            <Box sx={{ width: '1px', backgroundColor: '#D9D9D9', mx: 1, display: { xs: 'block', md: 'none' } }} />
                        )}
                        {po.price_info && (
                            <Box sx={{ gap: 0.5, display: 'flex', flexDirection: 'column' }}>
                                {po.price_info.map(item => (
                                    <Box key={item.product_id} sx={{ display: 'flex', gap: 2 }}>
                                        <Typography variant="labelM" sx={{ minWidth: {md: '100px'}, color: '#666' }}>
                                            {item.label}
                                        </Typography>
                                        <Typography variant="p3" sx={{ color: item.type == 'success' ? '#008000': '#333'}} >
                                            {item.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: { md: 'flex-end' }, minWidth: { md: '200px' }}}>
                        {po.status !== ORDER_STATUS.CANCELLED && po.status !== ORDER_STATUS.COMPLETED && (
                            <CustomButton 
                                variant="filled" 
                                size="small"
                                color={'primary'}
                                onClick={() =>
                                    po.status === ORDER_STATUS.PENDING
                                        ? OnPressEditPO(po)
                                        : OnPressCreateDP()
                                }
                                label={po.status === 'PENDING' ? 'Edit PO' : 'Create DP'}
                            />
                        )}
                        {po.status !== ORDER_STATUS.CANCELLED && (
                            <CustomButton
                                variant='text'
                                size="small"
                                color={'primary'}
                                onClick={() =>
                                    po.status === ORDER_STATUS.PENDING
                                        ? onCancelPO(po)
                                        : onViewDPAttached(po)
                                }
                                label={po.status === ORDER_STATUS.PENDING ? 'Cancel PO' : 'View Attached DP'}
                            />
                        )}
                    </Box>
                </Box>
        </Box>
    );
    return (
        <Box sx={{ p: { xs: 2, md: 3 }, }}>
            <Box>
                <Entity
                entityLabel="SAP Code*"
                showEntitySelect={true}
                forceRefresh={true}
                gridSizes={{
                    entity: { xs: 12, sm: 6, md: 4 }
                }}
                />
            </Box>
            <Box 
                sx={{ 
                    display: 'flex',
                    gap: 2,
                    flexDirection: 'column',
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, overflowY:'scroll' }}>
                    {dateFilters.map(filter => (
                        <CustomChip
                            key={filter.value}
                            label={filter.label}
                            onClick={() => setDateFilter(filter.value)}
                            onDelete={dateFilter === filter.value ? () => setDateFilter('') : undefined}
                            selected={dateFilter === filter.value}
                            variant={dateFilter === filter.value ? "filled" : "outlined"}
                        />
                    ))}
                    {statusOptions.map((status) => (
                        <CustomChip
                            key={status}
                            label={statusLabels[status]}
                            onClick={() => handleStatusFilter(status)}
                            onDelete={selectedStatus.includes(status) ? () => handleStatusFilter(status) : undefined}
                            selected={selectedStatus.includes(status)}
                            variant={selectedStatus.includes(status) ? "filled" : "outlined"}
                        />
                    ))}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2}}>
                    {poList.length > 0 ? (
                        poList.map((po, index) => renderPOItem(po, index))
                    ) : (
                        !loading && (
                            <Box sx={{ textAlign: 'center', p: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                                No purchase orders found
                            </Typography>
                            </Box>
                        )
                    )}
                </Box>
                {loading && poList.length === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
            </Box>
            <EditPOModal
                open={openEditPOModal}
                onClose={() => setOpenEditPOModal(false)}
                poDetails={poDetails}
                selectedSupplierData={selectedSupplierData}
                onSave={handleEditOrder}
                loading={loading}
            />
            <DeleteModal
                open={openDeletePOModal}
                onClose={() => setOpenDeletePOModal(false)}
                onConfirm={handleDeletePO}
                loading={loading}
                illustrationTitle="Are you sure you want to cancel this PO?"
                illustrationSubtitle="This action cannot be undone. All data would be lost."
            /> 
            <ViewAttachedDP
                open={openViewAttachedDP}
                onClose={() => setOpenViewAttachedDP(false)}
                poDetails={poDetails}
                
            />
        </Box>

    );
};

export default POList;